import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import LoadingSpinner from "../Common/LoadingSpinner";
import toast from "react-hot-toast";

function AssignmentQuizPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  // State management
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [assignment, setAssignment] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(null);

  // Fetch assignment data
  useEffect(() => {
    const fetchAssignment = async () => {
      try {
        const response = await axios.get(`/api/assignments/${id}`);
        const assignmentData = response.data;

        // Verify it's a quiz or exam
        if (assignmentData.type !== "quiz" && assignmentData.type !== "exam") {
          toast.error("This assignment is not a quiz or exam");
          navigate(`/assignments/${id}`);
          return;
        }

        setAssignment(assignmentData);

        // Initialize timer if time limit exists
        if (assignmentData.quizSettings?.timeLimit) {
          setTimeRemaining(assignmentData.quizSettings.timeLimit * 60); // Convert minutes to seconds
        }

        // Initialize answers object
        const initialAnswers = {};
        assignmentData.questions.forEach((_, index) => {
          initialAnswers[index] = null;
        });
        setAnswers(initialAnswers);

        setLoading(false);
      } catch (error) {
        console.error("Error fetching assignment:", error);
        toast.error("Failed to load quiz");
        navigate(`/assignments/${id}`);
      }
    };

    fetchAssignment();
  }, [id, navigate]);

  // Timer countdown effect
  useEffect(() => {
    if (timeRemaining === null || timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          // Time's up - auto submit
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining]);

  // Format time remaining as MM:SS
  const formatTime = (seconds) => {
    if (seconds === null) return "No time limit";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins} min ${secs.toString().padStart(2, "0")} sec`;
  };

  // Handle answer selection/input
  const handleAnswerChange = (questionIndex, answer) => {
    setAnswers((prev) => ({
      ...prev,
      [questionIndex]: answer,
    }));
  };

  // Navigate to previous question
  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  // Navigate to next question
  const handleNext = () => {
    if (currentQuestionIndex < assignment.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  // Submit quiz answers
  const handleSubmit = async () => {
    setSubmitting(true);

    try {
      // Format answers for submission
      const formattedAnswers = assignment.questions.map((question, index) => ({
        questionId: question._id || index,
        answer: answers[index],
        type: question.type, // Include question type (multiple-choice or written)
      }));

      const payload = {
        assignmentType: assignment.type, // Include assignment type (quiz, exam, homework, project, presentation)
        answers: formattedAnswers,
        submittedAt: new Date().toISOString(),
      };

      const repsonse = await axios.post(`/api/submissions/${id}`, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      console.log("Submission response:", repsonse.data);

      toast.success("Quiz submitted successfully!");
      navigate(`/assignments/${id}`);
    } catch (error) {
      console.error("Error submitting quiz:", error);
      toast.error(error.response?.data?.message || "Failed to submit quiz");
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return <LoadingSpinner />;
  }

  // No assignment found
  if (!assignment) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Assignment not found</p>
      </div>
    );
  }

  const currentQuestion = assignment.questions[currentQuestionIndex];
  const isLastQuestion =
    currentQuestionIndex === assignment.questions.length - 1;
  const isFirstQuestion = currentQuestionIndex === 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Back button (disabled) - Left */}
            <button
              disabled
              className="btn btn-secondary opacity-50 cursor-not-allowed"
              title="Cannot exit during quiz"
            >
              <ChevronLeftIcon className="h-5 w-5 mr-1" />
              Back
            </button>

            {/* Quiz Title - Center */}
            <h1 className="text-xl font-bold text-gray-900 text-center flex-1 mx-4">
              {assignment.title}
            </h1>

            {/* Timer - Right */}
            <div
              className={`px-4 py-2 rounded-lg font-mono font-semibold ${
                timeRemaining !== null && timeRemaining < 300
                  ? "bg-red-100 text-red-700"
                  : "bg-blue-100 text-blue-700"
              }`}
            >
              {formatTime(timeRemaining)}
            </div>
          </div>
        </div>
      </div>

      {/* Question Card */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="card">
          {/* Question Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-500">
                Question {currentQuestionIndex + 1} of{" "}
                {assignment.questions.length}
              </span>
              <span className="text-sm font-medium text-gray-500">
                {currentQuestion.points}{" "}
                {currentQuestion.points === 1 ? "point" : "points"}
              </span>
            </div>

            {/* Progress bar */}
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${
                    ((currentQuestionIndex + 1) / assignment.questions.length) *
                    100
                  }%`,
                }}
              />
            </div>
          </div>

          {/* Question Text */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {currentQuestion.questionText}
            </h2>

            {/* Multiple Choice Options */}
            {currentQuestion.type === "multiple-choice" && (
              <div className="space-y-3">
                {currentQuestion.options.map((option, optionIndex) => (
                  <label
                    key={optionIndex}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      answers[currentQuestionIndex] === optionIndex
                        ? "border-primary-600 bg-primary-50"
                        : "border-gray-200 hover:border-gray-300 bg-white"
                    }`}
                  >
                    <input
                      type="radio"
                      name={`question-${currentQuestionIndex}`}
                      checked={answers[currentQuestionIndex] === optionIndex}
                      onChange={() =>
                        handleAnswerChange(currentQuestionIndex, optionIndex)
                      }
                      className="h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                    />
                    <span className="ml-3 text-gray-900">{option}</span>
                  </label>
                ))}
              </div>
            )}

            {/* Written Answer Textarea */}
            {currentQuestion.type === "written" && (
              <textarea
                value={answers[currentQuestionIndex] || ""}
                onChange={(e) =>
                  handleAnswerChange(currentQuestionIndex, e.target.value)
                }
                className="input w-full"
                rows={8}
                placeholder="Type your answer here..."
              />
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between pt-6 border-t border-gray-200">
            {/* Previous Button - Hidden on first question */}
            {!isFirstQuestion ? (
              <button
                type="button"
                onClick={handlePrevious}
                className="btn btn-secondary flex items-center"
              >
                <ChevronLeftIcon className="h-5 w-5 mr-1" />
                Previous
              </button>
            ) : (
              <div></div> // Empty div to maintain spacing
            )}

            {/* Next or Submit Button */}
            {!isLastQuestion ? (
              <button
                type="button"
                onClick={handleNext}
                className="btn btn-primary flex items-center"
              >
                Next
                <ChevronRightIcon className="h-5 w-5 ml-1" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="btn btn-primary disabled:opacity-50"
              >
                {submitting ? "Submitting..." : "Submit Quiz"}
              </button>
            )}
          </div>
        </div>

        {/* Answer Status Overview */}
        <div className="mt-6 card">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">
            Answer Progress
          </h3>
          <div className="flex flex-wrap gap-2">
            {assignment.questions.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentQuestionIndex(index)}
                className={`w-10 h-10 rounded-lg font-medium text-sm transition-colors ${
                  currentQuestionIndex === index
                    ? "bg-primary-600 text-white"
                    : answers[index] !== null && answers[index] !== ""
                    ? "bg-green-100 text-green-700 hover:bg-green-200"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
                title={`Question ${index + 1}${
                  answers[index] !== null && answers[index] !== ""
                    ? " (Answered)"
                    : " (Not answered)"
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-3">
            {
              Object.values(answers).filter((a) => a !== null && a !== "")
                .length
            }{" "}
            of {assignment.questions.length} questions answered
          </p>
        </div>
      </div>
    </div>
  );
}

export default AssignmentQuizPage;
